import _axios from "../plugins/axios"
import dayjs, { Dayjs } from "dayjs"
import { useEffect, useState, useMemo,createContext, useContext } from "react"
import { generatePath, Link, useHistory } from "react-router-dom"
import { ActionSection, Row, Section, Table, TableWrap} from "../common/components"
import { API_URL } from "../common/constant"
import ROUTES from "../common/routes"
import { Patient, Schedule } from "../common/types"

interface Props {
  patient: Patient | null
}

interface ClinicShifts {
  AM: string;
  EVENING: string;
  PM: string;
}

const TODAY = dayjs();
const isSunday = TODAY.day() === 0;

// 西元格式轉換
const fmtISO = (day: Dayjs) => day.format("YYYY-MM-DD");

// 民國格式轉換
const fmtROC = (day: Dayjs) => {
  const year = day.year() - 1911;
  const month = (day.month()+1).toString().padStart(2, "0");
  const date = day.date().toString().padStart(2, "0");
  return `${year}-${month}-${date}`;
}

// 產生索引
const range = (num: number) => Array.from({length: num }, (_, idx) => idx);

// 取 週一到週六
const monToSat = (weekOffset: number): Dayjs[] => {
  const thisSunday = TODAY.startOf("week");
  const targetSunday = thisSunday.add(weekOffset, "week");
  // 回傳該週的週一到週六
  return Array.from({length: 6}, (_, idx) => targetSunday.add(idx + 1, "day"));
}

// 判斷今日診間時段是否已過期
const isShiftExpired = (date: Dayjs, shift: string, clinicShifts: ClinicShifts) => {
  if (!date.isSame(TODAY, 'day')) return false;

  const currentMinutes = TODAY.hour() * 60 + TODAY.minute();
  // 將 "HH:MM:SS" 格式轉換成總分鐘數
  const timeToMinutes = (timeStr: string): number => {
    const [hour, minute] = timeStr.split(':').map(Number);
    return hour * 60 + minute;
  };

  const shiftMap: Record<string, keyof ClinicShifts> = {
    "上午": "AM",
    "下午": "PM",
    "晚上": "EVENING",
  }
  const shiftKey = shiftMap[shift];

  if (!shiftKey) return false;
  return currentMinutes >= timeToMinutes(clinicShifts[shiftKey])
};

// 取 今天之後到週六的日期
function getRestOfThisWeekDates(): Dayjs[] {
  if (isSunday) return monToSat(0); // 週日登入看下一週

  const dayOfWeek = TODAY.day();
  const daysLeft = 6 - dayOfWeek + 1;
  return Array.from({length: daysLeft}, (_, idx) => TODAY.add(idx, "day"));
}

// 取 下週一到週六的日期
function getNextWeekDates(): Dayjs[] {
  return monToSat(1);
}

// 將 schedules 做成 key
function useScheduleIdx (schedules: Schedule[]) {
  return useMemo(() => {
    const schMap = new Map<string, Schedule[]>();
    for(const sch of schedules) {
      // isOpened = 0 不開放預約, isNormalClinicRoom 非一般診間不顯示
      const isNormalClinicRoom = sch.hixClinicRoomId.startsWith('0');
      if (sch.isOpened === 0 || !isNormalClinicRoom) continue;
      const dateKey = sch.schDate.slice(0, 10);
      const key = `${dateKey}|${sch.refShiftName}`;
      const arr = schMap.get(key);
      if (arr) arr.push(sch);
      else schMap.set(key, [sch]);
    }
    return schMap;
  }, [schedules]);
}

// 表格渲染
function ScheduleCell ({
  date,
  shift,
  index,
  idxMap,
}:{
  date: Dayjs;
  shift: string;
  index: number;
  idxMap: Map<string, Schedule[]>;
}) {
  const clinicShifts = useClinicShifts();

  if (isShiftExpired(date, shift, clinicShifts)) {
    return <td key={index} />;
  }
  const key = `${fmtISO(date)}|${shift}`;
  const items = idxMap.get(key);
  if (!items || items.length === 0) return <td key={index} />;

  return (
    <td key={index}>
      <div style={{display: "flex", flexDirection: "column", gap: 4}}>
        {items.map((item) => (
          <div key={item.id} style={{lineHeight: 1.2}}>
            <Link to={generatePath(ROUTES.makeAppointmentPage, {id: item.id})}>
              {item.hixClinicRoomName}({item.flagOpenClose === "9" ? "停診" : item.nextViewNo})
            </Link>
            <br />
            <span>{item.doctorName}</span>
          </div>
        ))}
      </div>
    </td>
  );
}

// 日期列
function DateHeaderRow ({ blanks, dates } : { blanks?: number; dates: Dayjs[] }) {
  return (
    <tr>
      <td/>
      {blanks ? range(blanks).map((idx) => <td key={`blank-${idx}`} />) : null}
      {dates.map((date, idx) => (
        <td key={`date-${idx}`}>{fmtROC(date)}</td>
      ))}
    </tr>
  );
}

// 時段列
function ShiftRow ({
  label,
  blanks,
  dates,
  idxMap,
  className,
}: {
  label: string;
  blanks?: number;
  dates: Dayjs[];
  idxMap: Map<string, Schedule[]>;
  className: string;
}) {
  return (
    <tr className={className}>
      <td>{label}</td>
      {blanks ? range(blanks).map((idx) => <td key={`blank-${label}-${idx}`} />) : null }
      {dates.map((date, idx) => (
        <ScheduleCell key={`${label}-${idx}`} date={date} shift={label} index={idx} idxMap={idxMap} />
      ))}
    </tr>
  );
}

async function getScheduleList () {
  const dateFrom = TODAY;
  const dateTo = TODAY.add(1, "week").endOf("week");

  const res = await _axios.post(API_URL.scheduleList,
    {
      dateFrom: dateFrom.format("YYYY-MM-DD"),
      dateTo: dateTo.format("YYYY-MM-DD"),
      flagType: "1",
      getNextViewNo: true,
    })

  return res;
}

async function getClinicShifts () {
  const res = await _axios.post(API_URL.getRefShift)

  return res;
}

const ClinicShiftsContext = createContext<ClinicShifts | null>(null);

export const useClinicShifts = () => {
  const context = useContext(ClinicShiftsContext);
  if (!context) throw new Error("useClinicShifts must be used within Provider");
  return context;
};

export default function ScheduleList(props: Props) {
  const history = useHistory();
  const { patient } = props;
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [clinicShifts, setClinicShifts] = useState<ClinicShifts>({ AM: "08:00:00", EVENING: "18:00:00", PM: "14:00:00" });

  const blanksBeforeThisWeek = isSunday ? 0 : TODAY.day() - 1; // 今日以前的空格數（週一到今日，包含今日）
  const thisWeekDates = getRestOfThisWeekDates();
  const nextWeekDates = getNextWeekDates();
  const scheduleIndex = useScheduleIdx(schedules);

  useEffect(() => {
    if (!patient) {
      history.replace(ROUTES.home);
      return;
    }
    (async () => {
      const response = await getScheduleList();
      setSchedules(response.data.result);
      // isOpened: 1-可預約, 0-不可預約
    })();
  }, [patient, history]);

  useEffect(() => {
    (async () => {
      const response = await getClinicShifts();
      const shifts: (keyof ClinicShifts)[] = ['AM', 'PM', 'EVENING'];
      const updatedShifts = response.data.result.reduce((acc: ClinicShifts, shiftItem: any) => {
        if (shifts.includes(shiftItem.id)) {
          acc[shiftItem.id as keyof ClinicShifts] = shiftItem.startTime;
        }
        return acc;
      }, {} as ClinicShifts)
      setClinicShifts(prev => ({ ...prev, ...updatedShifts}));
    })();
  }, [])

  return (
    <ClinicShiftsContext.Provider value={clinicShifts}>
      <main style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: "16px" }}>
        <ActionSection>
          <Link to={ROUTES.reservationList}>查詢或取消預約</Link>
          <Link to={ROUTES.home}>重新登入</Link>
        </ActionSection>

        <Section>
          <Row>使用者:{patient?.name}</Row>
          <Row style={{ textAlign: "center" }}>
            　可預約診次　(　)內數字為目前預估的預約號
          </Row>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>時段</th>
                  <th>星期一</th>
                  <th>星期二</th>
                  <th>星期三</th>
                  <th>星期四</th>
                  <th>星期五</th>
                  <th>星期六</th>
                </tr>
              </thead>

              <tbody>
                {/* 本週（今天之後到週六） */}
                <DateHeaderRow blanks={blanksBeforeThisWeek} dates={thisWeekDates} />
                <ShiftRow label="上午" blanks={blanksBeforeThisWeek} dates={thisWeekDates} idxMap={scheduleIndex} className="yellow" />
                <ShiftRow label="下午" blanks={blanksBeforeThisWeek} dates={thisWeekDates} idxMap={scheduleIndex} className="blue" />
                <ShiftRow label="晚上" blanks={blanksBeforeThisWeek} dates={thisWeekDates} idxMap={scheduleIndex} className="red" />

                {/* 下週（一到六） */}
                <DateHeaderRow dates={nextWeekDates} />
                <ShiftRow label="上午" dates={nextWeekDates} idxMap={scheduleIndex} className="yellow" />
                <ShiftRow label="下午" dates={nextWeekDates} idxMap={scheduleIndex} className="blue" />
                <ShiftRow label="晚上" dates={nextWeekDates} idxMap={scheduleIndex} className="red" />

                <tr>
                  <th>時段</th>
                  <th>星期一</th>
                  <th>星期二</th>
                  <th>星期三</th>
                  <th>星期四</th>
                  <th>星期五</th>
                  <th>星期六</th>
                </tr>
              </tbody>
            </Table>
          </TableWrap>
        </Section>
      </main>
    </ClinicShiftsContext.Provider>
  );
}